// Sentry init must happen before anything else loads so the error
// boundary catches even module-init failures. Audit HIGH 1.2 / 3.1.
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: import.meta.env['VITE_SENTRY_DSN'],
  environment: import.meta.env['VITE_SENTRY_ENVIRONMENT']
    ?? (import.meta.env.DEV ? 'development' : 'production'),
  release: import.meta.env['VITE_SENTRY_RELEASE'],
  // No performance monitoring yet — enable when a baseline is set.
  tracesSampleRate: 0,
  beforeSend(event) {
    // Same scrub the api's beforeSend applies (EVII retention audit):
    // if any integration ever attaches a request body, credential and
    // conversation-bearing fields must not ride to Sentry. The browser
    // SDK rarely populates request.data, so this is a cheap guarantee.
    if (event.request?.data && typeof event.request.data === 'object') {
      const data = event.request.data as Record<string, unknown>;
      delete data['password'];
      delete data['message'];
      delete data['text'];
      delete data['transcript'];
    }
    return event;
  },
});

// Cloudflare Web Analytics — production builds only. Injects the
// beacon script tag at runtime so local dev (where import.meta.env.PROD
// is false) never loads cloudflareinsights.com. Same conditional shape
// the Sentry environment tag uses above, applied at DOM level.
//
// The token is non-secret — Cloudflare's beacon design embeds it
// publicly in every analytics-instrumented site. Anyone inspecting
// the deployed page sees it. Safe to commit.
//
// Why runtime injection over an HTML <script> tag with prod-only
// gating: Vite's index.html interpolation doesn't expose
// import.meta.env.PROD as a templatable value (only VITE_*
// environment variables, which can't conditionally include/exclude
// elements). The plugin alternatives (vite-plugin-html, html-inject
// hooks) add a dependency for one script tag. Runtime injection
// matches the Sentry.init pattern above and stays within main.tsx's
// existing module-level setup style.
if (import.meta.env.PROD) {
  const cfBeacon = document.createElement('script');
  cfBeacon.defer = true;
  cfBeacon.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  cfBeacon.setAttribute(
    'data-cf-beacon',
    '{"token": "435aeae3dad14e859a08db5346fd9036"}',
  );
  document.head.appendChild(cfBeacon);
}

// Microsoft Clarity — session analytics, env-driven. Same runtime-
// injection shape as the Cloudflare beacon above (Vite's index.html
// can't conditionally include a tag), but keyed on VITE_CLARITY_ID
// rather than PROD: the env var IS the gate AND selects which Clarity
// project receives data, so staging and production point at different
// project ids and an unset env (local dev, un-provisioned prod) loads
// nothing at all. Clarity masks input-field text by default; the lead-
// capture email fields also carry an explicit clarity-mask class.
const clarityId = import.meta.env['VITE_CLARITY_ID'] as string | undefined;
if (clarityId) {
  const w = window as unknown as Record<string, unknown>;
  if (!w['clarity']) {
    const fn = (...args: unknown[]): void => {
      const q = ((fn as { q?: unknown[] }).q ??= []);
      q.push(args);
    };
    w['clarity'] = fn;
  }
  const clarityScript = document.createElement('script');
  clarityScript.async = true;
  clarityScript.src = `https://www.clarity.ms/tag/${clarityId}`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(clarityScript, firstScript);
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@evi/skins/styles.css';
import { DRIVER_MODE_CHOICE_KEY, ThemeProvider } from '@evi/skins';
import './lib/theme.css';
// Shared @keyframes (un-mangled global names) — referenced by
// CSS Modules across the PWA. Currently: evi-sweep (Total Points
// progress bar) and evi-inset-pulse-glow (Power Flow fill).
// Adding new shared animations here keeps consumers DRY. Names
// here must NOT collide with keyframes defined in @evi/skins or
// in lib/theme.css — the animations.css load order means whatever
// is defined here wins, and a name collision silently stomps the
// other consumer's animation (see 42b240c regression: the lift
// reused `evi-pulse-glow`, which the skins package owned).
import './styles/animations.css';
// i18n init (side-effect): sets up i18next with bundled locale resources and
// the device/stored starting language before React mounts. The server-side
// preference overrides it post-auth via useLocaleSync.
import './lib/i18n';
import { App } from './App';
import { initInstallPrompt } from '@/lib/pwaInstall';
import { initAppUpdateWatch } from '@/lib/appUpdate';

// Defensive cleanup: the admin app's auth key (`evi-auth`) has no
// business existing on the PWA's origin localStorage. The Vite
// configs are now hard-pinned to distinct ports (PWA :5173, admin
// :5174) via strictPort, so cross-app localStorage sharing should
// be impossible going forward — but this removeItem call cleans
// up any orphan key from before that pin landed, on every boot.
// removeItem is a no-op when the key is absent.
try {
  localStorage.removeItem('evi-auth');
} catch {
  // SSR / private-mode / quota-exceeded — none of which apply on
  // the PWA in practice, but the swallow is defensive against
  // localStorage throwing during boot. The cleanup is best-effort.
}

// A friend link's ?ref= is stashed before anything renders, so it survives the sign-in whichever way that goes
// (lib/pendingReferral.ts). App drains it once a session exists.
import { captureReferralFromUrl } from '@/lib/pendingReferral';
captureReferralFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// C3.2 — Session-state SSE stream initializer. Module-level (matches
// the Sentry.init / queryClient setup style above) so the connection
// lifecycle starts the moment the JS bundle loads and the auth store
// has rehydrated from localStorage. Push events fan out to React
// Query cache invalidations; polling hooks read connection status
// from `useSessionStreamStore` to back off their intervals when push
// is healthy.
import { initSessionEventStream } from '@/lib/sessionEventStream';
initSessionEventStream(queryClient);

// ── PWA install + service worker ──────────────────────────────────────────
// Capture the (Android/desktop) install prompt at boot — it can fire before
// React mounts; the Settings "Install app" button reads it. See lib/pwaInstall.
initInstallPrompt();

// Update watcher — resume-triggered version check + reload-while-hidden.
// The counterpart of the no-store index.html: headers protect every
// NAVIGATION, this protects the long-lived WINDOW that never navigates
// (the installed-PWA month-stale-bundle failure). See lib/appUpdate.
initAppUpdateWatch();

// Register the service worker (public/sw.js): no caching — it exists to
// satisfy Chrome's install criteria AND to receive web push.
//
// PROD only by default, so the Vite dev server isn't shadowed by a worker.
// VITE_ENABLE_SW=true opts a dev build in, because push CANNOT be tested
// without a registered worker and the alternative is testing the transport
// only in staging. The prod condition itself is unchanged: this widens the
// gate, it does not move it.
//
// Deferred to load so it never competes with first paint; guarded for
// browsers without SW support.
const SW_ENABLED = import.meta.env.PROD || import.meta.env['VITE_ENABLE_SW'] === 'true';
if (SW_ENABLED && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Non-fatal — install is a progressive enhancement, not required to run.
    });
  });
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    {/* Sentry.ErrorBoundary wraps the whole tree so render-time
        crashes get captured. Fallback is plain copy to avoid
        leaking error details to drivers; the real diagnostic
        info lives in Sentry. */}
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 24, fontFamily: 'system-ui', textAlign: 'center' }}>
          <h1 style={{ fontSize: 18, marginBottom: 8 }}>Something went wrong.</h1>
          <p style={{ fontSize: 14, color: '#888', marginBottom: 16 }}>
            Reload the app to try again. We&rsquo;ve been notified.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              fontSize: 14,
              border: '1px solid #888',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
        </div>
      }
    >
      {/* First-load theme (Warren, 21 October 2026): the driver's saved choice wins; otherwise the device setting,
          light when it has none. index.html's first-paint script decides the same way. */}
      <ThemeProvider followSystemMode storageModeKey={DRIVER_MODE_CHOICE_KEY}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
